import { uniqBy } from 'lodash';

import { isDocumentProjectType, isLongFormProjectType } from 'src/data/project';

import { GENERATION_TOOL } from './const';
import {
  getToolClassifiedMixtureSubGenerationTools,
  getToolDescription,
  getToolMixtureSubGenerationTools,
  getToolTitle,
  isMixtureTool,
} from './toolProps';
import { isBlankLongFormGenerationTool } from './utils';

const OPTIONS = [
  { subheader: 'Landing page' },
  { tool: GENERATION_TOOL.landingHeadline },
  { tool: GENERATION_TOOL.landingSubheader },
  { tool: GENERATION_TOOL.landingSectionHeadline },
  { tool: GENERATION_TOOL.landingCTA },
  { tool: GENERATION_TOOL.landingBenefits },
  { tool: GENERATION_TOOL.landingFeatures },
  { subheader: 'Website' },
  { tool: GENERATION_TOOL.landingSEOMetaTitle },
  { tool: GENERATION_TOOL.landingSEOMetadata },
  { subheader: 'Blog' },
  { tool: GENERATION_TOOL.articleBlogHeadline },
  { tool: GENERATION_TOOL.blogPostSectionSubHeadline },
  { tool: GENERATION_TOOL.blogPostSectionHeadline },
  { tool: GENERATION_TOOL.blogPostIntroParagraph },
  { subheader: 'Email' },
  { tool: GENERATION_TOOL.emailSubject },
  { tool: GENERATION_TOOL.emailHeadline },
  { tool: GENERATION_TOOL.emailSectionHeadline },
  { tool: GENERATION_TOOL.emailBody },
  { tool: GENERATION_TOOL.emailIntroParagraph },
  { tool: GENERATION_TOOL.emailCta },
  { tool: GENERATION_TOOL.emailMixture },
  { subheader: 'Social' },
  { tool: GENERATION_TOOL.instagramCaption },
  { tool: GENERATION_TOOL.facebookPost },
  { tool: GENERATION_TOOL.linkedinPost },
  { tool: GENERATION_TOOL.tweet },
  { tool: GENERATION_TOOL.youtubeTitle },
  { tool: GENERATION_TOOL.youtubeDescription },
  { subheader: 'Ads' },
  { tool: GENERATION_TOOL.facebookMixtureAd },
  { tool: GENERATION_TOOL.twitterMixtureAd },
  { tool: GENERATION_TOOL.googleRSA },
  { tool: GENERATION_TOOL.googleHeadlineProduct },
  { tool: GENERATION_TOOL.googleLongHeadlineProduct },
  { tool: GENERATION_TOOL.googleShortDescriptionProduct },
  { tool: GENERATION_TOOL.googleDescriptionProduct },
  { tool: GENERATION_TOOL.outbrainHeadlineProduct },
  { tool: GENERATION_TOOL.taboolaHeadlineProduct },
  { tool: GENERATION_TOOL.linkedinMixtureAd },
  { tool: GENERATION_TOOL.pinterestMixtureAd },
  { subheader: 'Ecommerce' },
  { tool: GENERATION_TOOL.amazonProductListing },
  { tool: GENERATION_TOOL.walmartProductListing },
  { tool: GENERATION_TOOL.generalProductListing },
  { tool: GENERATION_TOOL.basicProductReview },
  { subheader: 'Other' },
  { tool: GENERATION_TOOL.smsPromotional },
];

const SECTION_HEADLINE_TOOLS_TO_EXCLUDE = [
  GENERATION_TOOL.blogPostSectionHeadline,
  GENERATION_TOOL.landingSectionHeadline,
  GENERATION_TOOL.emailSectionHeadline,
];

const TOOLS_INCOMPATIBLE_WITH_BLANK_TEMPLATE = [
  GENERATION_TOOL.emailColdOutreachBody,
  GENERATION_TOOL.emailPromotionalBody,
  GENERATION_TOOL.emailCta,
  GENERATION_TOOL.emailHeadline,
  GENERATION_TOOL.landingCTA,
];

const TOOLS_INCOMPATIBLE_WITH_LONG_FORM = [
  GENERATION_TOOL.emailBody,
  GENERATION_TOOL.googleLongHeadlineProduct,
  GENERATION_TOOL.googleShortDescriptionProduct,
];

const TOOLS_INCOMPATIBLE_WITH_SHORT_FORM = [
  GENERATION_TOOL.blogPostIntroParagraph,
  GENERATION_TOOL.blogPostSectionSubHeadline,
  GENERATION_TOOL.emailIntroParagraph,
  GENERATION_TOOL.emailCta,
  GENERATION_TOOL.emailHeadline,
  GENERATION_TOOL.emailBody,
  GENERATION_TOOL.googleLongHeadlineProduct,
  GENERATION_TOOL.googleShortDescriptionProduct,
];

const TOOLS_INCOMPATIBLE_WITH_FORMAT_POPUP = [GENERATION_TOOL.googleRSA];

/**
 * Retrieves sub-tools for a given tool.
 * @param {GENERATION_TOOL} tool - The tool for which to get sub-tools.
 * @returns {GENERATION_TOOL[]} - Array of sub-tools.
 */
const getToolSubTools = (tool) => {
  const subTools =
    tool === GENERATION_TOOL.emailMixture
      ? getToolClassifiedMixtureSubGenerationTools(tool)
      : getToolMixtureSubGenerationTools(tool);

  return subTools.map((tool) => ({
    tool,
  }));
};

/**
 * Retrieves section tool options based on filtering criteria.
 * @param {Object} [options] - Options for filtering.
 * @param {PROJECT_TYPE} [options.projectType] - project type
 * @param {GENERATION_TOOL} [options.generationTool] - The main generation tool.
 * @param {boolean} [options.withFreeStyle=false] - Whether to include freestyle writing.
 * @param {boolean} [options.withoutSubHeaders=false] - Whether to exclude subheaders.
 * @param {boolean} [options.removeSectionHeadlines=false] - Whether to exclude section headlines.
 * @returns {Array<{ tool?: GENERATION_TOOL, subheader?: string }>} - Array of filtered generation tool options.
 */
export const getSectionToolsOptions = ({
  projectType,
  generationTool,
  withFreeStyle,
  withoutSubHeaders,
  removeSectionHeadlines,
} = {}) => {
  let filteredOptions = OPTIONS;

  if (removeSectionHeadlines) {
    filteredOptions = filteredOptions.filter(
      (option) => !SECTION_HEADLINE_TOOLS_TO_EXCLUDE.includes(option.tool)
    );
  }
  if (withFreeStyle) {
    filteredOptions = [
      { subheader: 'General writing' },
      { tool: GENERATION_TOOL.freestyle },
      ...filteredOptions,
    ];
  }

  // filter tools for "long-form" projects
  if (isLongFormProjectType(projectType)) {
    filteredOptions = filteredOptions.filter(
      (option) => !TOOLS_INCOMPATIBLE_WITH_LONG_FORM.includes(option.tool)
    );

    filteredOptions = filteredOptions.reduce((result, option) => {
      if (isMixtureTool(option.tool)) {
        let subTools = getToolSubTools(option.tool);

        // Filter out incompatible subTools if the generation tool is blank
        if (isBlankLongFormGenerationTool(generationTool)) {
          subTools = subTools.filter(
            (subTool) => !TOOLS_INCOMPATIBLE_WITH_BLANK_TEMPLATE.includes(subTool.tool)
          );
        }

        return [...result, ...subTools];
      }

      return [...result, option];
    }, []);
  }

  // filter tools for "short-form" projects
  if (isDocumentProjectType(projectType)) {
    filteredOptions = filteredOptions.filter(
      (option) => !TOOLS_INCOMPATIBLE_WITH_SHORT_FORM.includes(option.tool)
    );
  }

  // filter tools for "format" popup
  if (!projectType) {
    filteredOptions = filteredOptions.filter(
      (option) => !TOOLS_INCOMPATIBLE_WITH_FORMAT_POPUP.includes(option.tool)
    );

    filteredOptions = filteredOptions.reduce((result, option) => {
      if (isMixtureTool(option.tool)) {
        const subTools = getToolSubTools(option.tool).filter(
          (subTool) => !TOOLS_INCOMPATIBLE_WITH_BLANK_TEMPLATE.includes(subTool.tool)
        );

        return [...result, ...subTools];
      }

      return [...result, option];
    }, []);
  }

  if (withoutSubHeaders) {
    filteredOptions = filteredOptions.filter((option) => option.tool);
  }

  return uniqBy(filteredOptions, ({ tool, subheader }) => tool || subheader);
};

/**
 * Prepare "tool list" object for backend
 * @param {GENERATION_TOOL[]} tools - tools
 * @returns {object} - tool list object
 */
export const prepareToolListForBackend = (tools) => {
  return tools.reduce((toolList, tool) => {
    const title = getToolTitle(tool);
    const description = getToolDescription(tool);
    toolList[tool] = `${title}${description ? ` - ${description}` : ''}`;
    return toolList;
  }, {});
};

/**
 * Get available generation tools object for the backend.
 * @param {Object} [options] - Options for filtering generation tools.
 * @param {PROJECT_TYPE} [options.projectType] - Whether the project is a long-form project.
 * @param {GENERATION_TOOL} [options.generationTool] - A specific generation tool to filter by.
 * @returns {Object} - The prepared tool list object.
 */
export const getAvailableGenerationTools = ({ projectType, generationTool } = {}) => {
  const filteredToolItems = getSectionToolsOptions({
    projectType,
    generationTool,
    withoutSubHeaders: true,
    removeSectionHeadlines: true,
  });

  // take only "tool" values
  const tools = filteredToolItems.map((toolItem) => toolItem.tool);

  if (isDocumentProjectType(projectType)) {
    tools.push(GENERATION_TOOL.googlePerformanceMaxAd);
  }

  return prepareToolListForBackend(tools);
};
